/**
 * RENDERABLE certificate template (printable HTML → system PDF).
 *
 * The content comes from the server-emitted snapshot normalized by
 * `buildCertificateContent` (@space/shared): student name, course, sealed
 * version, issuing academy, date and public verification id/URL.
 *
 * Space Academy's "diploma" design: thin double-line frame, brand in a solid
 * chip (logo theme-safe pattern), name in large display with an amber rule
 * and star seal. Theme-aware on screen and sober when printed.
 */
import { msg } from "@lingui/core/macro";
import { type Component, Show } from "solid-js";
import type { CertificateContent } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { LogoMark } from "@/components/ui/logo";

export const CertificateCard: Component<{ content: CertificateContent }> = (props) => {
  const { t } = useI18n();
  return (
    <div
      data-certificate-print
      data-testid="certificate-card"
      class="relative overflow-hidden rounded-2xl border border-separator bg-surface p-8 text-center shadow-lg md:p-12"
    >
      {/* Thin double frame (classic engraving) */}
      <div class="pointer-events-none absolute inset-3 rounded-xl border border-separator" />
      <div class="pointer-events-none absolute inset-4 rounded-lg border border-separator/50" />

      {/* Amber star seal (bottom-right corner) */}
      <svg
        class="pointer-events-none absolute bottom-8 right-8 hidden h-16 w-16 text-warning md:block"
        viewBox="0 0 64 64"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="32" cy="32" r="27" stroke="currentColor" stroke-width="1.5" opacity="0.55" />
        <circle
          cx="32"
          cy="32"
          r="22.5"
          stroke="currentColor"
          stroke-width="1"
          stroke-dasharray="2 3"
          opacity="0.5"
        />
        <path
          d="M32 18l3.4 8.8 8.8 3.4-8.8 3.4L32 42.4l-3.4-8.8-8.8-3.4 8.8-3.4Z"
          fill="currentColor"
          opacity="0.85"
        />
      </svg>

      <div class="relative flex flex-col items-center gap-4">
        {/* Brand legible in any theme (solid chip, logo theme-safe pattern) */}
        <span class="inline-flex items-center gap-2.5 rounded-lg bg-accent px-4 py-2 text-accent-foreground">
          <LogoMark class="h-5 w-5" />
          <span class="font-display text-sm font-bold tracking-[0.08em]">SPACE ACADEMY</span>
        </span>

        <div class="flex w-full items-center justify-center gap-4">
          <span class="h-px max-w-24 flex-1 bg-separator" aria-hidden="true" />
          <p class="text-xs font-bold uppercase tracking-[0.28em] text-muted-foreground">
            {t(msg`Certificate of completion`)}
          </p>
          <span class="h-px max-w-24 flex-1 bg-separator" aria-hidden="true" />
        </div>

        <p class="mt-2 text-sm text-muted-foreground">{t(msg`This certifies that`)}</p>
        <h1
          class="font-display text-3xl font-extrabold tracking-tight md:text-4xl"
          data-testid="certificate-learner"
        >
          {props.content.learnerName}
        </h1>
        {/* Amber rule under the name */}
        <span class="h-0.5 w-24 rounded-full bg-warning" aria-hidden="true" />

        <p class="text-sm text-muted-foreground">{t(msg`has successfully completed the course`)}</p>
        <h2
          class="font-display text-xl font-bold text-accent md:text-2xl"
          data-testid="certificate-course"
        >
          {props.content.courseTitle}
        </h2>

        <Show when={props.content.orgName}>
          {(org) => (
            <p class="text-sm font-medium" data-testid="certificate-org">
              {t(msg`Issued by {org}`, { org: org() })}
            </p>
          )}
        </Show>

        {/* F3-B: final grade SEALED at cohort close (if the course was graded). */}
        <Show when={props.content.finalGradeLabel}>
          {(grade) => (
            <p
              class="rounded-full bg-warning/15 px-4 py-1 text-sm font-bold text-foreground"
              data-testid="certificate-final-grade"
            >
              {t(msg`Final grade: {grade}`, { grade: grade() })}
            </p>
          )}
        </Show>

        <div class="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span data-testid="certificate-date">{props.content.issuedAtLabel}</span>
          <span data-testid="certificate-version">
            {t(msg`Course content {version}`, { version: props.content.versionLabel })}
          </span>
        </div>

        {/* Public verification credential */}
        <div class="mt-4 w-full rounded-xl border border-separator/60 bg-surface-secondary px-4 py-3 text-xs text-muted-foreground">
          <p class="font-semibold uppercase tracking-wide">{t(msg`Verify this certificate`)}</p>
          <p class="mt-1 break-all font-mono" data-testid="certificate-verify-url">
            {props.content.verifyUrl}
          </p>
          <p class="mt-1 break-all font-mono opacity-80" data-testid="certificate-id">
            {t(msg`ID: {certId}`, { certId: props.content.certId })}
          </p>
        </div>
      </div>
    </div>
  );
};
